/**
 * Status badge (shadcn Badge + lucide icon, RM-117). Semantic color/label map
 * per JobStatus; emits data-testid="status-badge" + data-status. Base UI Badge
 * forwards both via mergeProps and auto-sizes the leading icon to size-3.
 */
import {
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  Server,
  UploadCloud,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import type { JobStatus } from '../lib/schemas'
import { Badge } from './ui/badge'
import { useTranslation } from 'react-i18next'

const STATUS: Record<
  JobStatus,
  { color: string; labelKey: string; icon: LucideIcon; spin?: boolean }
> = {
  preparing: {
    color: 'bg-warning/10 text-warning',
    labelKey: 'jobs.status.preparing',
    icon: Clock,
  },
  queued: {
    color: 'bg-warning/10 text-warning',
    labelKey: 'jobs.status.queued',
    icon: Clock,
  },
  provisioning: {
    color: 'bg-muted text-foreground',
    labelKey: 'jobs.status.provisioning',
    icon: Server,
  },
  rendering: {
    color: 'bg-muted text-foreground animate-pulse',
    labelKey: 'jobs.status.rendering',
    icon: Loader2,
    spin: true,
  },
  uploading: {
    color: 'bg-info/10 text-info',
    labelKey: 'jobs.status.uploading',
    icon: UploadCloud,
  },
  done: {
    color: 'bg-success/10 text-success',
    labelKey: 'jobs.status.done',
    icon: CheckCircle2,
  },
  delivering: {
    color: 'bg-info/10 text-info',
    labelKey: 'jobs.status.delivering',
    icon: UploadCloud,
  },
  delivered: {
    color: 'bg-success/10 text-success',
    labelKey: 'jobs.status.delivered',
    icon: CheckCircle2,
  },
  delivery_failed: {
    color: 'bg-destructive/10 text-destructive',
    labelKey: 'jobs.status.deliveryFailed',
    icon: XCircle,
  },
  failed: {
    color: 'bg-destructive/10 text-destructive',
    labelKey: 'jobs.status.failed',
    icon: XCircle,
  },
  cancelled: {
    color: 'bg-muted text-muted-foreground',
    labelKey: 'jobs.status.cancelled',
    icon: Ban,
  },
}

export function StatusBadge({ status }: { status: JobStatus }) {
  const { t } = useTranslation()
  const entry = STATUS[status] ?? {
    color: 'bg-muted text-muted-foreground',
    labelKey: status,
    icon: Clock,
  }
  const Icon = entry.icon
  return (
    <Badge
      variant="secondary"
      className={entry.color}
      data-testid="status-badge"
      data-status={status}
    >
      <Icon className={entry.spin ? 'animate-spin' : undefined} />
      {t(entry.labelKey)}
    </Badge>
  )
}
